import { GoogleIcon, AppleIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";
import { Button, Flex, Text } from "@radix-ui/themes";
import { signInWithRedirect } from "aws-amplify/auth";

interface SocialAuthButtonsProps {
  lastMethod?: string;
  /** Outlined white buttons: "modal" = compact (default), "landing" = taller */
  variant?: "modal" | "landing";
  /** Whether to show the "Or" divider above buttons */
  showDivider?: boolean;
  /** Label prefix for button text — e.g. "Continue" → "Continue with Google" */
  labelPrefix?: string;
}

export default function SocialAuthButtons({
  lastMethod,
  variant = "modal",
  showDivider = true,
  labelPrefix = "Continue",
}: SocialAuthButtonsProps) {
  const isGoogleLast = lastMethod === "google";
  const isAppleLast = lastMethod === "apple";

  const isLanding = variant === "landing";

  const buttonBase = cn(
    "w-full !bg-white !border !border-solid !border-blue-900 hover:!bg-gray-100 text-blue-900 cursor-pointer",
    isLanding
      ? "h-[48px] font-bold text-md"
      : "h-[40px] font-semibold text-[16px]",
  );

  const ringClass =
    "ring-1 ring-blue-500 ring-offset-2 ring-offset-background focus-visible:ring-blue-500";

  return (
    <>
      {showDivider && (
        <Text className="text-[16px] text-gray-8 text-center">or</Text>
      )}

      <Flex direction="column" gap={isLanding ? "1" : "2"} className="w-full">
        <div className="w-full">
          {isGoogleLast && (
            <span className="block text-right mb-1 text-[11px] font-medium text-text-tertiary">
              Last used
            </span>
          )}
          <Button
            onClick={() =>
              signInWithRedirect({
                provider: "Google",
                customState: "google",
                options: { prompt: "SELECT_ACCOUNT" },
              })
            }
            type="button"
            radius="full"
            className={cn(buttonBase, isGoogleLast && ringClass)}
          >
            <GoogleIcon className="inline-block mr-2" /> {labelPrefix} with
            Google
          </Button>
        </div>

        <div className="w-full">
          {isAppleLast && (
            <span className="block text-right mb-1 text-[11px] font-medium text-text-tertiary">
              Last used
            </span>
          )}
          <Button
            onClick={() =>
              signInWithRedirect({
                provider: "Apple",
                customState: "apple",
              })
            }
            type="button"
            radius="full"
            className={cn(buttonBase, isAppleLast && ringClass)}
          >
            <AppleIcon className="inline-block mr-2 [&_path]:fill-black" />{" "}
            {labelPrefix} with Apple
          </Button>
        </div>
      </Flex>
    </>
  );
}
